import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { I18nextProvider } from 'react-i18next'
import App from './App'
import { AuthProvider } from './context/AuthContext'
import { ToastProvider } from './context/ToastContext'
import { ThemeProvider } from './context/ThemeContext'
import { LanguageProvider } from './context/LanguageContext'
import { queryClient } from './lib/queryClient'
import { initSentry, SentryErrorBoundary, captureError } from './lib/sentry'
import i18n from './lib/i18n'
import './index.css'

// ============================================================================
// APP SUSPENSION CHECK — blocks everything before any provider loads
// ============================================================================
if (import.meta.env.VITE_APP_SUSPENDED === 'true') {
  const rootElement = document.getElementById('root')
  if (rootElement) {
    rootElement.innerHTML = ''
    createRoot(rootElement).render(
      <StrictMode>
        <SuspendedScreen />
      </StrictMode>
    )
  }
  // Stop execution — nothing else loads
  throw new Error('APP_SUSPENDED')
}

function SuspendedScreen(): JSX.Element {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
      padding: '24px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      {/* Subtle grid pattern overlay */}
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)',
        backgroundSize: '40px 40px',
        pointerEvents: 'none',
      }} />

      <div style={{
        maxWidth: '480px',
        width: '100%',
        position: 'relative',
      }}>
        {/* Main card */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          padding: '48px 36px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
        }}>
          {/* Icon */}
          <div style={{
            width: '72px',
            height: '72px',
            margin: '0 auto 28px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 40px rgba(220, 38, 38, 0.3)',
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
            </svg>
          </div>

          {/* English */}
          <h1 style={{
            fontSize: '26px',
            fontWeight: 700,
            color: '#f1f5f9',
            textAlign: 'center',
            margin: '0 0 12px',
            letterSpacing: '-0.02em',
          }}>
            Service Suspended
          </h1>
          <p style={{
            fontSize: '15px',
            color: '#94a3b8',
            textAlign: 'center',
            lineHeight: 1.7,
            margin: '0 0 28px',
          }}>
            This application has been temporarily suspended due to an outstanding invoice.
            Please contact the developer to resolve payment and restore access.
          </p>

          {/* Divider */}
          <div style={{
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
            margin: '0 0 28px',
          }} />

          {/* Romanian */}
          <h1 style={{
            fontSize: '26px',
            fontWeight: 700,
            color: '#f1f5f9',
            textAlign: 'center',
            margin: '0 0 12px',
            letterSpacing: '-0.02em',
          }}>
            Serviciu Suspendat
          </h1>
          <p style={{
            fontSize: '15px',
            color: '#94a3b8',
            textAlign: 'center',
            lineHeight: 1.7,
            margin: 0,
          }}>
            Aceast&#259; aplica&#539;ie a fost suspendat&#259; temporar din cauza unei facturi neachitate.
            V&#259; rug&#259;m s&#259; contacta&#539;i dezvoltatorul pentru a rezolva plata &#537;i a restabili accesul.
          </p>
        </div>

        {/* Footer note */}
        <p style={{
          fontSize: '13px',
          color: '#475569',
          textAlign: 'center',
          marginTop: '20px',
        }}>
          Service will be restored once payment is received.
          <br />
          Serviciul va fi restabilit dup&#259; primirea pl&#259;&#539;ii.
        </p>
      </div>
    </div>
  )
}

// Initialize Sentry error tracking
initSentry()

// Register service worker with auto-update
if ('serviceWorker' in navigator) {
  // Track interval for potential cleanup (though in practice this runs for app lifetime)
  let updateCheckInterval: ReturnType<typeof setInterval> | null = null

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(registration => {
        // Check for updates every 60 seconds
        updateCheckInterval = setInterval(() => {
          registration.update()
        }, 60000)

        // Listen for new service worker
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content available, reload automatically
                window.location.reload()
              }
            })
          }
        })
      })
      .catch(error => {
        // Report to Sentry for production monitoring
        captureError(
          error instanceof Error ? error : new Error('SW registration failed'),
          { component: 'ServiceWorker', registrationUrl: '/sw.js' }
        )
      })
  })

  // Listen for messages from service worker
  navigator.serviceWorker.addEventListener('message', event => {
    if (event.data?.type === 'SW_UPDATED') {
      window.location.reload()
    }
  })

  // Cleanup on page unload (prevents memory leaks during hot reload in development)
  window.addEventListener('beforeunload', () => {
    if (updateCheckInterval) {
      clearInterval(updateCheckInterval)
    }
  })
}

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element not found')
}

createRoot(rootElement).render(
  <StrictMode>
    <SentryErrorBoundary fallback={<ErrorFallback />}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ThemeProvider>
              <LanguageProvider>
                <ToastProvider>
                  <AuthProvider>
                    <App />
                  </AuthProvider>
                </ToastProvider>
              </LanguageProvider>
            </ThemeProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </I18nextProvider>
    </SentryErrorBoundary>
  </StrictMode>
)

// Error fallback component
function ErrorFallback(): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Something went wrong
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          We&apos;re sorry, an unexpected error occurred.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Reload Page
        </button>
      </div>
    </div>
  )
}
